import { useEffect, useRef, useState } from 'react'
import { IconSearch } from '@holbonkit/icons'
import {
    BottomSheet,
    BottomSheetBody,
    BottomSheetContent,
    BottomSheetHeader,
    BottomSheetTitle,
    BottomSheetTrigger,
    Box,
    Button,
    Input,
    Skeleton,
    Text,
} from '@holbonkit/ui'
import { useInfiniteQuery } from '@tanstack/react-query'

import { getCountryList } from '@/shared/api'

import type { TSelectedCountry } from './types'

import styles from './search-bottom-sheet.module.scss'

const PAGE_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 300

type TSearchBottomSheetProps = {
    errorClassName?: string
    errorText?: string
    label: string
    name?: string
    onBlur?: () => void
    onChange: (value: TSelectedCountry) => void
    value: TSelectedCountry
}

export const SearchBottomSheet = ({
    errorClassName,
    errorText,
    label,
    name,
    onBlur,
    onChange,
    value,
}: TSearchBottomSheetProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [searchName, setSearchName] = useState<string | undefined>(undefined)

    const listRef = useRef<HTMLDivElement>(null)
    const sentinelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const timeoutId = globalThis.setTimeout(() => {
            setSearchName(searchInput.length === 0 ? undefined : searchInput)
        }, SEARCH_DEBOUNCE_MS)

        return () => globalThis.clearTimeout(timeoutId)
    }, [searchInput])

    const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isPending } =
        useInfiniteQuery({
            queryKey: [{ url: '/api/v1/countries' }, { name: searchName, limit: PAGE_LIMIT }],
            queryFn: ({ pageParam }) =>
                getCountryList({
                    name: searchName,
                    limit: PAGE_LIMIT,
                    cursor: pageParam,
                }),
            initialPageParam: undefined as string | undefined,
            getNextPageParam: (lastPage) =>
                lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
            enabled: isOpen,
        })

    const countries = data?.pages.flatMap((page) => page.data) ?? []

    useEffect(() => {
        const root = listRef.current
        const sentinel = sentinelRef.current

        if (!root || !sentinel) {
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    void fetchNextPage()
                }
            },
            { root, rootMargin: '200px' },
        )

        observer.observe(sentinel)

        return () => observer.disconnect()
    }, [countries.length, fetchNextPage, hasNextPage, isFetchingNextPage])

    const resetSearch = () => {
        setSearchInput('')
        setSearchName(undefined)
    }

    const hasSelection = Boolean(value.code)

    const renderCountryListContent = () => {
        if (isPending) {
            return (
                <Box className={styles['country-list__status']}>
                    <Skeleton style={{ height: 46, borderRadius: 8 }} />
                    <Skeleton style={{ height: 46, borderRadius: 8 }} />
                    <Skeleton style={{ height: 46, borderRadius: 8 }} />
                </Box>
            )
        }

        if (isError) {
            return (
                <Text className={styles['country-list__status']} variant='body-m-16-medium'>
                    Не удалось загрузить список стран
                </Text>
            )
        }

        if (countries.length === 0) {
            return (
                <Text className={styles['country-list__status']} variant='body-m-16-medium'>
                    Ничего не найдено
                </Text>
            )
        }

        return countries.map((item) => (
            <Box
                key={item.id}
                className={styles['country-list__item']}
                data-test-id={`country-list-item-${item.id}`}
                onClick={() => {
                    onChange({ code: item.code, name: item.name })
                    resetSearch()
                    setIsOpen(false)
                }}
            >
                <Text variant='body-m-16-medium'>{item.name}</Text>
            </Box>
        ))
    }

    return (
        <Box className={styles['country-field']}>
            <BottomSheet
                open={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open)
                    if (!open) {
                        onBlur?.()
                    }
                }}
            >
                <BottomSheetTrigger>
                    <Button
                        className={`${styles['trigger-button']} ${errorText && styles['trigger-button--error']}`}
                        data-test-id={`address__country-${name ?? 'search'}`}
                        name={name}
                        size='lg'
                        type='button'
                        variant='secondary'
                    >
                        <Box>
                            {hasSelection ?
                                <Text
                                    as='label'
                                    className={styles['trigger-button--label']}
                                    variant='body-s-14-regular'
                                >
                                    {label}
                                </Text>
                            :   null}
                            <Text
                                className={`${styles['trigger-button--text']} ${hasSelection && styles['trigger-button--selected']} ${errorText && styles['trigger-button--error']}`}
                                variant='body-m-16-medium'
                            >
                                {hasSelection ? value.name : label}
                            </Text>
                        </Box>
                    </Button>
                </BottomSheetTrigger>
                <BottomSheetContent fullscreen>
                    <BottomSheetHeader className={styles['bottom-sheet__header']}>
                        <BottomSheetTitle>{label}</BottomSheetTitle>
                    </BottomSheetHeader>
                    <BottomSheetBody className={styles['bottom-sheet__body']}>
                        <Input
                            placeholder='Введите страну'
                            startIcon={<IconSearch />}
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                        <div ref={listRef} className={styles['country-list']}>
                            {renderCountryListContent()}
                            <div
                                ref={sentinelRef}
                                aria-hidden
                                className={styles['country-list__sentinel']}
                            />
                        </div>
                    </BottomSheetBody>
                </BottomSheetContent>
            </BottomSheet>
            {errorText ?
                <Text
                    as='p'
                    className={`${styles['country-field__error']} ${errorClassName ?? styles['country-field__error--padded']}`}
                    variant='body-s-14-regular'
                >
                    {errorText}
                </Text>
            :   null}
        </Box>
    )
}
